import { startTransition, useEffect, useMemo, useState } from "react"

import {
  PublicLookupSectionSkeleton,
  PublicSidebarSectionSkeleton,
} from "@/components/app/public-loading-skeleton"
import { Button } from "@/components/ui/button"
import {
  GUARDIAN_RELATIONSHIP_OPTIONS,
  REGISTRANT_RELATIONSHIP_OPTIONS,
  getActiveAdultNotice,
  getLookupCompliance,
  getNoticeSections,
  isStudentsSafetyKit,
} from "@/features/public/register/registration-compliance"
import {
  createRegistrationOtpChallengeState,
  failOtpChallenge,
  formatOtpCountdown,
  getOtpChallengeTimers,
  markOtpSending,
  markOtpVerified,
  markOtpVerifying,
  resetOtpChallenge,
  startOtpChallenge,
  type RegistrationOtpChallengeState,
} from "@/features/public/register/otp-challenge"
import {
  buildPublicRegistrationPayload,
  createInitialRegistrationFormState,
  mergeLookupIntoRegistrationForm,
  requiresGuardianApproval,
  validateRegistrationForm,
  type RegistrationFormState,
} from "@/features/public/register/registration-payload"
import { GuardianApprovalPage } from "@/features/public/register/guardian-approval-page"
import {
  getOrganizationIdFromLookup,
  type PublicScanSearch,
} from "@/features/public/shared/scan"
import {
  apiClient,
  getApiFailure,
  isApiError,
  type ConsentNoticeVersionSummary,
  type PublicLookupData,
  type SubjectAgeBand,
  type TagType,
} from "@/lib/api/client"
import {
  getPlantationLocalLanguageOption,
  PLANTATION_LOCAL_LANGUAGE_OPTIONS,
} from "@/lib/safetyqr-reporting-templates"

type LookupStatus = "idle" | "invalid" | "loading" | "ready"
type NoticeStatus = "error" | "loading" | "ready"

interface PublicRegisterPageProps {
  initialSearch?: PublicScanSearch
  initialTag?: string | null
}

interface ConfirmedRegistrationContext {
  form: RegistrationFormState
  lookup: PublicLookupData
}

interface RegistrationSuccessState {
  eyebrow: string
  message: string
  nextSteps: string[]
  statusLabel: string
  statusValue: string
  title: string
  verificationValue: string
}

async function resolveRegistrationContext(
  tag: string,
  draftForm: RegistrationFormState
): Promise<ConfirmedRegistrationContext> {
  const response = await apiClient.lookupPublicTag({
    intent: "registration",
    tag,
  })
  const confirmedForm = mergeLookupIntoRegistrationForm(
    {
      ...draftForm,
      deviceTag: response.data.tag,
      tagType: response.data.tagType ?? draftForm.tagType,
    },
    response.data
  )

  return {
    form: confirmedForm,
    lookup: response.data,
  }
}

const NOTICE_LOCALE = "en-IN"
const SECTION_CLASS = "shell-panel rounded-4xl border border-border/70 p-5 sm:p-6"
const INPUT_CLASS =
  "mt-2 w-full rounded-2xl border border-border bg-background/90 px-4 py-3 text-sm shadow-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/15"
const TEXTAREA_CLASS = `${INPUT_CLASS} min-h-28 resize-y`
const LABEL_CLASS = "text-sm font-medium text-foreground"
const BANNER_BASE = "rounded-3xl border px-4 py-3 text-sm leading-6"
const HELP_CLASS = "mt-2 text-xs leading-5 text-muted-foreground"

function formatTagTypeLabel(tagType: TagType) {
  if (tagType === "vehicle_sticker") {
    return "Vehicle sticker"
  }

  if (tagType === "personal_tag") {
    return "Personal tag"
  }

  return "Fleet tag"
}

function buildSuccessState(
  result: PublicLookupData,
  form: RegistrationFormState
): RegistrationSuccessState {
  const compliance = getLookupCompliance(result)
  const isPlantationsRegistration =
    result.kit?.productFamily === "site_safety_qr"
    && result.kit?.solutionCategory === "plantations"
  const pendingGuardianApproval =
    compliance?.registrationStatus === "pending_guardian_approval"
    || Boolean(compliance?.guardianRequired)

  if (pendingGuardianApproval) {
    return {
      eyebrow: "Registration pending",
      message:
        "This SafetyQR profile has been saved in pending state. It will not activate or verify public owner access until the listed parent or legal guardian approves it.",
      nextSteps: [
        "The profile remains pending and is not activated yet.",
        "Guardian approval must complete before public reporting activation or owner access is enabled.",
        "Keep the guardian contact details accurate so the approval flow can proceed without rework.",
      ],
      statusLabel: "Guardian status",
      statusValue: "Pending approval",
      title: "Guardian approval required",
      verificationValue: "Pending guardian approval",
    }
  }

  if (isPlantationsRegistration) {
    const selectedLanguageLabel =
      getPlantationLocalLanguageOption(form.preferredLanguageCode)?.label
      || form.preferredLanguageLabel
      || "English only"

    return {
      eyebrow: "Registration complete",
      message:
        "The Plantations kit is active, the shared site tags are ready, and this tag's zone name was saved for reporting.",
      nextSteps: [
        "The verified deployment details are now linked to this Plantations kit.",
        `Reporting now opens in English plus ${selectedLanguageLabel}.`,
        "Sibling tags now open the reporting flow instead of first-scan registration.",
        "Return later if zone or supervisor details need to be updated.",
      ],
      statusLabel: "Activation",
      statusValue: "Activated",
      title: "Plantations kit activated",
      verificationValue: "Adult OTP verified",
    }
  }

  if (form.tagType === "fleet_tag") {
    return {
      eyebrow: "Registration complete",
      message: "The fleet tag was verified and activated successfully.",
      nextSteps: [
        "The verified fleet contact is now linked to this tag.",
        "You can return later to correct or update the saved details.",
        "Keep the verified phone available for future owner access checks.",
      ],
      statusLabel: "Activation",
      statusValue: "Activated",
      title: "Fleet tag activated",
      verificationValue: "Adult OTP verified",
    }
  }

  return {
    eyebrow: "Registration complete",
    message: "The SafetyQR profile was verified and saved successfully.",
    nextSteps: [
      "Your verified details are now linked to this tag.",
      "You can update your profile again anytime if your information changes.",
      "Keep the verified phone available for future owner access checks.",
    ],
    statusLabel: "Activation",
    statusValue: "Activated",
    title: "Registration verified",
    verificationValue: "Adult OTP verified",
  }
}

function readGuardianApprovalMode() {
  if (typeof window === "undefined") {
    return false
  }

  return new URLSearchParams(window.location.search).get("mode") === "guardian-approval"
}

function buildGuardianApprovalLink(tag: string) {
  if (typeof window === "undefined") {
    return `/register?tag=${encodeURIComponent(tag)}&mode=guardian-approval`
  }

  const url = new URL(window.location.href)
  url.pathname = "/register"
  url.search = ""
  url.searchParams.set("tag", tag)
  url.searchParams.set("mode", "guardian-approval")
  return url.toString()
}

export function PublicRegisterPage({
  initialTag = null,
}: PublicRegisterPageProps) {
  const guardianApprovalMode = readGuardianApprovalMode()

  if (guardianApprovalMode) {
    return <GuardianApprovalPage initialTag={initialTag} />
  }

  return <PublicRegisterPageContent initialTag={initialTag} />
}

function PublicRegisterPageContent({
  initialTag = null,
}: PublicRegisterPageProps) {
  const [form, setForm] = useState<RegistrationFormState>(() =>
    createInitialRegistrationFormState(initialTag ?? "")
  )
  const [bootstrapLookupPending, setBootstrapLookupPending] = useState(
    () => Boolean(initialTag)
  )
  const [lookupStatus, setLookupStatus] = useState<LookupStatus>(
    initialTag ? "loading" : "idle"
  )
  const [lookupData, setLookupData] = useState<PublicLookupData | null>(null)
  const [lookupMessage, setLookupMessage] = useState<string>(
    initialTag
      ? "Checking the scanned tag..."
      : "Check the tag before requesting an OTP code."
  )
  const [tagLocked, setTagLocked] = useState(Boolean(initialTag))
  const [formError, setFormError] = useState("")
  const [otpCode, setOtpCode] = useState("")
  const [otpChallenge, setOtpChallenge] =
    useState<RegistrationOtpChallengeState>(
      createRegistrationOtpChallengeState
    )
  const [tickNow, setTickNow] = useState(() => Date.now())
  const [submitStatus, setSubmitStatus] = useState<"idle" | "saving">("idle")
  const [successState, setSuccessState] = useState<RegistrationSuccessState | null>(
    null
  )
  const [successLink, setSuccessLink] = useState("")
  const [adultNotice, setAdultNotice] =
    useState<ConsentNoticeVersionSummary | null>(null)
  const [guardianNotice, setGuardianNotice] =
    useState<ConsentNoticeVersionSummary | null>(null)
  const [noticeError, setNoticeError] = useState("")
  const [noticeStatus, setNoticeStatus] = useState<NoticeStatus>("loading")

  useEffect(() => {
    let cancelled = false

    async function loadComplianceNotices() {
      setNoticeStatus("loading")
      setNoticeError("")

      try {
        const [adultResponse, guardianResponse] = await Promise.all([
          apiClient.getActiveConsentNotices({
            audience: "adult",
            locale: NOTICE_LOCALE,
            surface: "registration",
          }),
          apiClient.getActiveConsentNotices({
            audience: "guardian",
            locale: NOTICE_LOCALE,
            surface: "guardian_approval",
          }),
        ])

        if (cancelled) {
          return
        }

        startTransition(() => {
          setAdultNotice(adultResponse.data[0] ?? null)
          setGuardianNotice(guardianResponse.data[0] ?? null)
          setNoticeStatus("ready")
        })
      } catch (error) {
        if (cancelled) {
          return
        }

        setNoticeStatus("error")
        setNoticeError(
          error instanceof Error
            ? error.message
            : "Unable to load the current compliance notices."
        )
      }
    }

    void loadComplianceNotices()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!otpChallenge.timerActive) {
      return
    }

    const timerId = window.setInterval(() => {
      setTickNow(Date.now())
    }, 1000)

    return () => {
      window.clearInterval(timerId)
    }
  }, [otpChallenge.timerActive])

  useEffect(() => {
    if (!initialTag) {
      return
    }

    const confirmedInitialTag = initialTag
    let cancelled = false

    async function confirmInitialTag() {
      setLookupStatus("loading")
      setLookupMessage("Checking the tag...")
      setFormError("")

      try {
        const confirmed = await resolveRegistrationContext(
          confirmedInitialTag,
          createInitialRegistrationFormState(confirmedInitialTag)
        )

        if (cancelled) {
          return
        }

        startTransition(() => {
          setLookupData(confirmed.lookup)
          setLookupStatus("ready")
          setLookupMessage(
            confirmed.lookup.scanDestination === "report" && confirmed.lookup.kit?.id
              ? "This SafetyQR kit is already activated. Reporting is now the default scan path for every sibling sticker."
              : "Issued tag confirmed. Review the registration details below."
          )
          setForm(confirmed.form)
          setBootstrapLookupPending(false)
        })
      } catch (error) {
        const failure = getApiFailure(error)

        if (cancelled) {
          return
        }

        if (isApiError(error) && failure?.code === "INVALID_TAG") {
          setLookupStatus("invalid")
          setLookupData(null)
          setLookupMessage(
            `The scanned tag "${confirmedInitialTag}" is invalid or has not been issued yet.`
          )
          setTagLocked(false)
          setBootstrapLookupPending(false)
          return
        }

        setLookupStatus("invalid")
        setLookupData(null)
        setLookupMessage(
          error instanceof Error
            ? error.message
            : "We could not confirm this SafetyQR tag right now."
        )
        setTagLocked(false)
        setBootstrapLookupPending(false)
      }
    }

    void confirmInitialTag()

    return () => {
      cancelled = true
    }
  }, [initialTag])

  const timers = useMemo(
    () => getOtpChallengeTimers(otpChallenge, tickNow),
    [otpChallenge, tickNow]
  )
  const resolvedTagType = lookupData?.tagType ?? null
  const tagTypeLocked = Boolean(resolvedTagType)
  const isPlantationsRegistration = useMemo(
    () =>
      lookupData?.kit?.productFamily === "site_safety_qr"
      && lookupData?.kit?.solutionCategory === "plantations",
    [lookupData]
  )
  const registrationBlocked = useMemo(
    () => Boolean(lookupData?.kit?.id) && lookupData?.scanDestination === "report",
    [lookupData]
  )
  const isBootstrapLookupPending =
    bootstrapLookupPending && lookupStatus === "loading" && !lookupData
  const isStudentsRegistration = useMemo(
    () => isStudentsSafetyKit(lookupData),
    [lookupData]
  )
  const guardianRequired = useMemo(
    () => requiresGuardianApproval(form, lookupData),
    [form, lookupData]
  )
  const activeAdultNotice = useMemo(
    () => getActiveAdultNotice(lookupData, adultNotice),
    [adultNotice, lookupData]
  )
  const adultNoticeSections = useMemo(
    () => getNoticeSections(activeAdultNotice),
    [activeAdultNotice]
  )
  const guardianNoticeSections = useMemo(
    () => getNoticeSections(guardianNotice),
    [guardianNotice]
  )
  const showsRegistrationGate = !registrationBlocked && !isPlantationsRegistration
  const needsAdultConsentNotice =
    !registrationBlocked && !isPlantationsRegistration && !guardianRequired
  const showsGuardianApprovalSection =
    !registrationBlocked && !isPlantationsRegistration && guardianRequired
  const adultNoticeUnavailable =
    needsAdultConsentNotice && noticeStatus !== "loading" && !activeAdultNotice

  useEffect(() => {
    setForm((currentForm) => {
      let nextForm = currentForm
      let changed = false

      if (isStudentsRegistration && currentForm.subjectAgeBand !== "under_18") {
        nextForm = {
          ...nextForm,
          subjectAgeBand: "under_18",
        }
        changed = true
      }

      if (guardianRequired) {
        if (nextForm.noticeVersionId || nextForm.consentAccepted) {
          nextForm = {
            ...nextForm,
            consentAccepted: false,
            noticeVersionId: "",
          }
          changed = true
        }

        return changed ? nextForm : currentForm
      }

      const resolvedNoticeVersionId = activeAdultNotice?.id ?? ""

      if (
        resolvedNoticeVersionId
        && nextForm.noticeVersionId !== resolvedNoticeVersionId
      ) {
        nextForm = {
          ...nextForm,
          consentAccepted: false,
          noticeVersionId: resolvedNoticeVersionId,
        }
        changed = true
      }

      return changed ? nextForm : currentForm
    })
  }, [activeAdultNotice?.id, guardianRequired, isStudentsRegistration])

  useEffect(() => {
    if (!guardianRequired) {
      return
    }

    if (!otpChallenge.verificationId && !otpChallenge.verified) {
      return
    }

    setOtpChallenge(() =>
      resetOtpChallenge(
        "Guardian-required registrations do not use the self-service OTP path."
      )
    )
    setOtpCode("")
  }, [guardianRequired, otpChallenge.verificationId, otpChallenge.verified])

  function updateForm<K extends keyof RegistrationFormState>(
    key: K,
    value: RegistrationFormState[K]
  ) {
    setForm((currentForm) => ({
      ...currentForm,
      [key]: value,
    }))
  }

  function handlePlantationLanguageChange(value: string) {
    const selectedLanguage = getPlantationLocalLanguageOption(value)

    setForm((currentForm) => ({
      ...currentForm,
      preferredLanguageCode: value,
      preferredLanguageLabel: selectedLanguage?.label ?? "",
    }))
  }

  async function confirmTag(
    tag: string,
    source: "manual" | "scan",
    draftForm: RegistrationFormState = form
  ): Promise<ConfirmedRegistrationContext | null> {
    const trimmedTag = tag.trim()

    if (!trimmedTag) {
      setFormError("Enter the SafetyQR tag before checking it.")
      return null
    }

    setLookupStatus("loading")
    setLookupMessage("Checking the tag...")
    setFormError("")

    try {
      const confirmed = await resolveRegistrationContext(trimmedTag, draftForm)

      startTransition(() => {
        setLookupData(confirmed.lookup)
        setLookupStatus("ready")
        setLookupMessage(
          confirmed.lookup.scanDestination === "report" && confirmed.lookup.kit?.id
            ? "This SafetyQR kit is already activated. Reporting is now the default scan path for every sibling sticker."
            : "Issued tag confirmed. Review the registration details below."
        )
        setForm(confirmed.form)
      })

      return confirmed
    } catch (error) {
      const failure = getApiFailure(error)

      if (isApiError(error) && failure?.code === "INVALID_TAG") {
        setLookupStatus("invalid")
        setLookupData(null)
        setLookupMessage(
          source === "scan"
            ? `The scanned tag "${trimmedTag}" is invalid or has not been issued yet.`
            : "This SafetyQR tag is invalid or has not been issued."
        )
        setTagLocked(false)
        return null
      }

      setLookupStatus("invalid")
      setLookupData(null)
      setLookupMessage(
        error instanceof Error
          ? error.message
          : "We could not confirm this SafetyQR tag right now."
      )
      setTagLocked(false)
      return null
    }
  }

  function resetOtp(message: string) {
    setOtpChallenge(resetOtpChallenge(message))
    setOtpCode("")
  }

  async function handleSendOtp() {
    if (guardianRequired) {
      setFormError(
        "Under-18 registrations require guardian approval and do not use the self-service OTP path."
      )
      return
    }

    const confirmed = await confirmTag(form.deviceTag, "manual", form)

    if (!confirmed) {
      return
    }

    if (confirmed.lookup.scanDestination === "report" && confirmed.lookup.kit?.id) {
      setFormError(
        "This SafetyQR kit is already activated. Use the report flow instead of registering again."
      )
      return
    }

    if (!confirmed.form.ownerPhone.trim()) {
      setFormError("Enter the primary contact phone number before requesting a code.")
      return
    }

    setFormError("")
    setOtpChallenge((currentChallenge) => markOtpSending(currentChallenge))

    try {
      const response = await apiClient.startPublicOtp({
        organizationId: getOrganizationIdFromLookup(confirmed.lookup),
        phoneNumber: confirmed.form.ownerPhone.trim(),
        stickerId: confirmed.form.deviceTag.trim(),
        tag: confirmed.form.deviceTag.trim(),
        tagType: confirmed.form.tagType,
      })

      setOtpCode("")
      setOtpChallenge((currentChallenge) =>
        startOtpChallenge(currentChallenge, {
          ...response.data,
          phoneNumber: confirmed.form.ownerPhone.trim(),
        })
      )
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unable to send the verification code."

      setOtpChallenge((currentChallenge) =>
        failOtpChallenge(currentChallenge, message)
      )
      setFormError(message)
    }
  }

  async function handleVerifyOtp() {
    if (!otpChallenge.verificationId) {
      setFormError("Request a code before trying to verify it.")
      return
    }

    if (otpCode.trim().length !== 6) {
      setFormError("Enter the six-digit verification code.")
      return
    }

    setFormError("")
    setOtpChallenge((currentChallenge) => markOtpVerifying(currentChallenge))

    try {
      const response = await apiClient.verifyPublicOtp({
        otp: otpCode.trim(),
        phoneNumber: form.ownerPhone.trim(),
        verificationId: otpChallenge.verificationId,
      })

      if (!response.data.approved) {
        throw new Error("That verification code was not accepted.")
      }

      setOtpChallenge((currentChallenge) => markOtpVerified(currentChallenge))
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unable to verify the code."

      setOtpChallenge((currentChallenge) =>
        failOtpChallenge(currentChallenge, message)
      )
      setFormError(message)
    }
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()

    const confirmed = await confirmTag(form.deviceTag, "manual", form)

    if (!confirmed) {
      return
    }

    if (confirmed.lookup.scanDestination === "report" && confirmed.lookup.kit?.id) {
      setFormError(
        "This SafetyQR kit is already activated. Use the report flow instead of registering again."
      )
      return
    }

    const validationErrors = validateRegistrationForm(
      confirmed.form,
      otpChallenge.verified,
      confirmed.lookup
    )
    if (validationErrors.length > 0) {
      setFormError(validationErrors[0])
      return
    }

    setFormError("")
    setSubmitStatus("saving")

    try {
      const payload = buildPublicRegistrationPayload(
        confirmed.form,
        confirmed.lookup
      )
      const response = await apiClient.submitPublicRegistration(payload)
      const nextSuccessState = buildSuccessState(response.data, confirmed.form)
      const responseCompliance = getLookupCompliance(response.data)

      startTransition(() => {
        setSuccessState(nextSuccessState)
        setSuccessLink(
          responseCompliance?.registrationStatus === "pending_guardian_approval"
            ? buildGuardianApprovalLink(response.data.tag)
            : ""
        )
      })
    } catch (error) {
      setFormError(
        error instanceof Error
          ? error.message
          : "Unable to save the SafetyQR registration."
      )
      setSubmitStatus("idle")
    }
  }

  if (successState) {
    return (
      <section className="grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(18rem,0.8fr)]">
        <div className={SECTION_CLASS}>
          <span className="text-xs font-semibold tracking-[0.2em] text-muted-foreground uppercase">
            {successState.eyebrow}
          </span>
          <h2 className="mt-3 font-heading text-3xl text-balance">
            {successState.title}
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-muted-foreground">
            {successState.message}
          </p>
          <dl className="mt-6 grid gap-4 sm:grid-cols-2">
            <SummaryStat label="Tag" value={form.deviceTag} />
            <SummaryStat label="Profile type" value={form.tagType} />
            <SummaryStat label="Primary phone" value={form.ownerPhone} />
            <SummaryStat
              label={successState.statusLabel}
              value={successState.statusValue}
            />
            <SummaryStat label="Verification" value={successState.verificationValue} />
          </dl>
          {successLink ? (
            <div className="mt-6 rounded-3xl border border-amber-300/70 bg-amber-100/60 p-4 text-sm leading-6 text-amber-950">
              <strong className="block font-medium">
                Share this guardian approval link
              </strong>
              <p className="mt-2 break-all font-mono text-xs leading-5">
                {successLink}
              </p>
            </div>
          ) : null}
        </div>

        <aside className={SECTION_CLASS}>
          <h3 className="font-semibold">What happens next</h3>
          <ul className="mt-4 space-y-3 text-sm leading-6 text-muted-foreground">
            {successState.nextSteps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ul>
        </aside>
      </section>
    )
  }

  return (
    <section className="grid gap-5 xl:grid-cols-[minmax(0,1.24fr)_minmax(18rem,0.82fr)]">
      <form className="space-y-5" onSubmit={handleSubmit}>
        {isBootstrapLookupPending ? (
          <PublicLookupSectionSkeleton testId="public-register-lookup-skeleton" />
        ) : (
          <section className={SECTION_CLASS}>
            <span className="text-xs font-semibold tracking-[0.2em] text-muted-foreground uppercase">
              Public registration
            </span>
            <h2 className="mt-3 font-heading text-3xl text-balance">
              Register your SafetyQR device.
            </h2>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-muted-foreground">
              {isPlantationsRegistration
                ? "Activate the Plantations kit on the first successful scan. This saves the owner and supervisor details, stores the plantation context, and names the scanned tag's zone in one verified request."
                : "Check the tag, choose the registration path, and complete the compliance steps before saving the profile."}
            </p>

            <div className="mt-6 grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,12rem)]">
              <label className="block">
                <span className={LABEL_CLASS}>SafetyQR tag</span>
                <input
                  className={INPUT_CLASS}
                  name="deviceTag"
                  onChange={(event) => {
                    setFormError("")
                    setLookupStatus("idle")
                    setLookupMessage("Check the tag before requesting an OTP code.")
                    setLookupData(null)
                    setTagLocked(false)
                    updateForm("deviceTag", event.target.value)
                    resetOtp("Tag changed. Request a fresh code for this registration.")
                  }}
                  readOnly={tagLocked}
                  value={form.deviceTag}
                />
                <p className={HELP_CLASS}>
                  {tagLocked
                    ? "Captured from the scanned SafetyQR tag."
                    : "Enter the printed tag code and check it before continuing."}
                </p>
              </label>

              <label className="block">
                <span className={LABEL_CLASS}>Profile type</span>
                {tagTypeLocked ? (
                  <>
                    <div
                      className={`${INPUT_CLASS} flex items-center text-foreground`}
                      role="status"
                      aria-live="polite"
                    >
                      {formatTagTypeLabel(resolvedTagType ?? form.tagType)}
                    </div>
                    <p className={HELP_CLASS}>
                      This tag already resolves to a single profile type, so it cannot be changed.
                    </p>
                  </>
                ) : (
                  <select
                    className={INPUT_CLASS}
                    name="tagType"
                    onChange={(event) => {
                      updateForm("tagType", event.target.value as TagType)
                      resetOtp("Profile type changed. Request a fresh code for this registration.")
                    }}
                    value={form.tagType}
                  >
                    <option value="vehicle_sticker">Vehicle sticker</option>
                    <option value="personal_tag">Personal tag</option>
                    <option value="fleet_tag">Fleet tag</option>
                  </select>
                )}
              </label>
            </div>

            <div className="mt-4 flex items-center gap-3">
              <Button
                disabled={lookupStatus === "loading"}
                onClick={() => void confirmTag(form.deviceTag, "manual")}
                type="button"
                variant="outline"
              >
                Check tag
              </Button>
            </div>

            <div
              className={`${BANNER_BASE} mt-4 ${
                lookupStatus === "invalid"
                  ? "border-rose-300/70 bg-rose-100/70 text-rose-950"
                  : lookupStatus === "ready"
                    ? "border-emerald-300/70 bg-emerald-100/70 text-emerald-950"
                    : "border-border/70 bg-background/70 text-muted-foreground"
              }`}
            >
              {lookupMessage}
            </div>
          </section>
        )}

        {showsRegistrationGate ? (
          <section className={SECTION_CLASS}>
            <h3 className="font-semibold">Registration path</h3>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-muted-foreground">
              {isStudentsRegistration
                ? "Students SafetyQR registrations always stay in guardian-required mode. The profile remains pending until the listed parent or legal guardian approves it."
                : "Choose the subject age band and your relationship to the person or asset being registered."}
            </p>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <SelectField
                disabled={isStudentsRegistration}
                label="Subject age band"
                name="subjectAgeBand"
                onChange={(value) => {
                  const nextAgeBand = value as SubjectAgeBand
                  updateForm("subjectAgeBand", nextAgeBand)
                  setFormError("")
                  resetOtp(
                    nextAgeBand === "under_18"
                      ? "Guardian-required registrations do not use the self-service OTP path."
                      : "Age band changed. Request a fresh code for adult verification."
                  )
                }}
                options={[
                  { label: "18 or older", value: "adult_18_plus" },
                  { label: "Under 18", value: "under_18" },
                ]}
                value={form.subjectAgeBand}
              />
              <SelectField
                label="Your relationship to this profile"
                name="registrantRelationship"
                onChange={(value) => updateForm("registrantRelationship", value)}
                options={[
                  { label: "Select", value: "" },
                  ...REGISTRANT_RELATIONSHIP_OPTIONS,
                ]}
                value={form.registrantRelationship}
              />
            </div>

            {guardianRequired ? (
              <div className={`${BANNER_BASE} mt-5 border-amber-300/70 bg-amber-100/60 text-amber-950`}>
                <strong className="block font-medium">
                  Guardian approval is required before activation
                </strong>
                <p className="mt-2">
                  This registration will be saved in pending state. The minor profile
                  will not be presented as verified or activated until the parent or
                  legal guardian approval step completes.
                </p>
              </div>
            ) : null}
          </section>
        ) : null}

        <section className={SECTION_CLASS}>
          <h3 className="font-semibold">
            {registrationBlocked ? "Registration is no longer needed" : "Primary contact"}
          </h3>
          {registrationBlocked ? (
            <>
              <div className={`${BANNER_BASE} mt-4 border-amber-300/70 bg-amber-100/60 text-amber-950`}>
                <strong className="block font-medium">
                  This SafetyQR kit is already activated
                </strong>
                <p className="mt-2">
                  First-scan registration has already been completed for this kit. Any
                  sibling sticker now opens reporting by default, so registration should
                  not be repeated from this tag.
                </p>
              </div>

              <div className="mt-5 grid gap-4 md:grid-cols-2">
                <SummaryStat label="Next step" value="Open /report for the active flow" />
                <SummaryStat
                  label="Activation"
                  value={lookupData?.activationState ?? "activated"}
                />
              </div>
            </>
          ) : (
            <>
              <div className="mt-5 grid gap-4 md:grid-cols-3">
                <TextField
                  label={
                    isPlantationsRegistration
                      ? "Owner or primary contact name"
                      : guardianRequired
                        ? "Registrant name"
                        : form.tagType === "fleet_tag"
                          ? "Primary contact name"
                          : "Owner full name"
                  }
                  name="ownerName"
                  onChange={(value) => updateForm("ownerName", value)}
                  type="text"
                  value={form.ownerName}
                />
                <TextField
                  label={
                    isPlantationsRegistration
                      ? "Owner or primary contact phone"
                      : guardianRequired
                        ? "Registrant phone"
                        : form.tagType === "fleet_tag"
                          ? "Primary contact phone"
                          : "Owner phone"
                  }
                  name="ownerPhone"
                  onChange={(value) => {
                    updateForm("ownerPhone", value)

                    if (!guardianRequired) {
                      resetOtp("Phone number changed. Request a fresh code for this number.")
                    }
                  }}
                  type="tel"
                  value={form.ownerPhone}
                />
                <TextField
                  label={
                    isPlantationsRegistration
                      ? "Owner or primary contact email"
                      : guardianRequired
                        ? "Registrant email"
                        : form.tagType === "fleet_tag"
                          ? "Primary contact email"
                          : "Owner email"
                  }
                  name="ownerEmail"
                  onChange={(value) => updateForm("ownerEmail", value)}
                  type="email"
                  value={form.ownerEmail}
                />
              </div>

              {form.tagType === "fleet_tag" ? (
                <div className="mt-5 grid gap-4 md:grid-cols-2">
                  <TextField
                    label="Fleet name"
                    name="fleetName"
                    onChange={(value) => updateForm("fleetName", value)}
                    type="text"
                    value={form.fleetName}
                  />
                  <TextField
                    label="Company name"
                    name="companyName"
                    onChange={(value) => updateForm("companyName", value)}
                    type="text"
                    value={form.companyName}
                  />
                </div>
              ) : null}
            </>
          )}
        </section>

        {needsAdultConsentNotice ? (
          <section className={SECTION_CLASS}>
            <h3 className="font-semibold">Consent notice</h3>
            {noticeStatus === "loading" ? (
              <div className={`${BANNER_BASE} mt-4 border-border/70 bg-background/70 text-muted-foreground`}>
                Loading the current registration consent notice...
              </div>
            ) : adultNoticeUnavailable ? (
              <div className={`${BANNER_BASE} mt-4 border-rose-300/70 bg-rose-100/70 text-rose-950`}>
                {noticeError || "No active adult registration consent notice is available."}
              </div>
            ) : (
              <>
                <NoticeCard
                  notice={activeAdultNotice}
                  sections={adultNoticeSections}
                />
                <label className="mt-5 flex items-start gap-3 rounded-3xl border border-border/70 bg-background/70 p-4 text-sm leading-6 text-muted-foreground">
                  <input
                    checked={form.consentAccepted}
                    onChange={(event) =>
                      updateForm("consentAccepted", event.target.checked)
                    }
                    type="checkbox"
                  />
                  <span>
                    I have reviewed the active SafetyQR registration consent notice
                    and consent to this use of my submitted registration data.
                  </span>
                </label>
              </>
            )}
          </section>
        ) : null}

        {!registrationBlocked && !guardianRequired ? (
          <section className={SECTION_CLASS}>
            <h3 className="font-semibold">OTP verification</h3>
            <div
              className={`${BANNER_BASE} mt-4 ${
                otpChallenge.tone === "success"
                  ? "border-emerald-300/70 bg-emerald-100/70 text-emerald-950"
                  : otpChallenge.tone === "error"
                    ? "border-rose-300/70 bg-rose-100/70 text-rose-950"
                    : otpChallenge.tone === "pending"
                      ? "border-amber-300/70 bg-amber-100/70 text-amber-950"
                      : "border-border/70 bg-background/70 text-muted-foreground"
              }`}
            >
              <strong className="block font-medium">{otpChallenge.title}</strong>
              <p className="mt-1">{otpChallenge.description}</p>
              {otpChallenge.verificationId && !otpChallenge.verified ? (
                <p className="mt-2 text-xs">
                  Code expires in {formatOtpCountdown(timers.expiresRemainingMs)}.
                </p>
              ) : null}
              {otpChallenge.debugOtp ? (
                <p className="mt-2 text-xs">
                  Development code: {otpChallenge.debugOtp}
                </p>
              ) : null}
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-[minmax(0,1fr)_auto_auto]">
              <label className="block">
                <span className={LABEL_CLASS}>Verification code</span>
                <input
                  className={INPUT_CLASS}
                  inputMode="numeric"
                  name="otpCode"
                  onChange={(event) =>
                    setOtpCode(event.target.value.replace(/\D+/g, "").slice(0, 6))
                  }
                  value={otpCode}
                />
              </label>

              <div className="self-end">
                <Button
                  disabled={
                    otpChallenge.sendLoading
                    || otpChallenge.verified
                    || timers.resendRemainingMs > 0
                  }
                  onClick={handleSendOtp}
                  type="button"
                  variant="outline"
                >
                  {otpChallenge.sendLoading
                    ? "Sending..."
                    : timers.resendRemainingMs > 0
                      ? `Resend in ${formatOtpCountdown(timers.resendRemainingMs)}`
                      : otpChallenge.verificationId
                        ? "Resend code"
                        : "Send code"}
                </Button>
              </div>

              <div className="self-end">
                <Button
                  disabled={
                    otpChallenge.verifyLoading
                    || otpChallenge.verified
                    || otpCode.trim().length !== 6
                  }
                  onClick={handleVerifyOtp}
                  type="button"
                >
                  {otpChallenge.verifyLoading ? "Verifying..." : "Verify code"}
                </Button>
              </div>
            </div>
          </section>
        ) : null}

        {showsGuardianApprovalSection ? (
          <section className={SECTION_CLASS}>
            <h3 className="font-semibold">Guardian approval</h3>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-muted-foreground">
              Save the registration with the parent or legal guardian details below.
              The profile will remain pending until the guardian approval flow is completed.
            </p>

            {guardianNotice ? (
              <NoticeCard
                notice={guardianNotice}
                sections={guardianNoticeSections}
              />
            ) : (
              <div className={`${BANNER_BASE} mt-4 border-amber-300/70 bg-amber-100/60 text-amber-950`}>
                The active guardian approval notice could not be loaded, but the
                registration can still be saved in pending state with the required
                guardian contact details.
              </div>
            )}

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <TextField
                label="Parent or legal guardian name"
                name="guardianName"
                onChange={(value) => updateForm("guardianName", value)}
                type="text"
                value={form.guardianName}
              />
              <TextField
                label="Parent or legal guardian phone"
                name="guardianPhone"
                onChange={(value) => updateForm("guardianPhone", value)}
                type="tel"
                value={form.guardianPhone}
              />
              <TextField
                label="Guardian email"
                name="guardianEmail"
                onChange={(value) => updateForm("guardianEmail", value)}
                type="email"
                value={form.guardianEmail}
              />
              <SelectField
                label="Guardian relationship"
                name="guardianRelationship"
                onChange={(value) => updateForm("guardianRelationship", value)}
                options={[
                  { label: "Select", value: "" },
                  ...GUARDIAN_RELATIONSHIP_OPTIONS,
                ]}
                value={form.guardianRelationship}
              />
            </div>
          </section>
        ) : null}

        {!registrationBlocked && isPlantationsRegistration ? (
          <section className={SECTION_CLASS}>
            <h3 className="font-semibold">Plantation activation details</h3>
            <div className="mt-5 grid gap-4 md:grid-cols-3">
              <TextField
                label="Supervisor name"
                name="emergencyContactName"
                onChange={(value) => updateForm("emergencyContactName", value)}
                type="text"
                value={form.emergencyContactName}
              />
              <TextField
                label="Supervisor phone"
                name="emergencyContactPhone"
                onChange={(value) => updateForm("emergencyContactPhone", value)}
                type="tel"
                value={form.emergencyContactPhone}
              />
              <TextField
                label="Supervisor email"
                name="emergencyContactEmail"
                onChange={(value) => updateForm("emergencyContactEmail", value)}
                type="email"
                value={form.emergencyContactEmail}
              />
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-3">
              <TextField
                label="Plantation ID"
                name="plantationId"
                onChange={(value) => updateForm("plantationId", value)}
                type="text"
                value={form.plantationId}
              />
              <TextField
                label="Plantation name"
                name="plantationName"
                onChange={(value) => updateForm("plantationName", value)}
                type="text"
                value={form.plantationName}
              />
              <TextField
                label="Zone name for this tag"
                name="zoneName"
                onChange={(value) => updateForm("zoneName", value)}
                type="text"
                value={form.zoneName}
              />
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
              <div>
                <SelectField
                  label="Local reporting language"
                  name="preferredLanguageCode"
                  onChange={handlePlantationLanguageChange}
                  options={[
                    { label: "Select local language", value: "" },
                    ...PLANTATION_LOCAL_LANGUAGE_OPTIONS.map((option) => ({
                      label: option.label,
                      value: option.code,
                    })),
                  ]}
                  value={form.preferredLanguageCode}
                />
              </div>
              <div className={`${BANNER_BASE} self-end border-border/70 bg-background/70 text-muted-foreground`}>
                English stays visible by default. Choose one local language so the
                Plantation reporting flow opens in dual language without adding extra
                steps during panic or incident reporting.
              </div>
            </div>
          </section>
        ) : !registrationBlocked ? (
          <section className={SECTION_CLASS}>
            <h3 className="font-semibold">Profile details</h3>
            {form.tagType !== "personal_tag" ? (
              <div className="mt-5 grid gap-4 md:grid-cols-2">
                <SelectField
                  label="Vehicle type"
                  name="vehicleType"
                  onChange={(value) => updateForm("vehicleType", value)}
                  options={[
                    { label: "Select", value: "" },
                    { label: "Car", value: "car" },
                    { label: "SUV", value: "suv" },
                    { label: "Pickup", value: "pickup" },
                    { label: "Motorcycle", value: "motorcycle" },
                    { label: "Van", value: "van" },
                    { label: "Truck", value: "truck" },
                    { label: "Bus", value: "bus" },
                    { label: "Other", value: "other" },
                  ]}
                  value={form.vehicleType}
                />
                <TextField
                  label="Plate number"
                  name="plateNumber"
                  onChange={(value) => updateForm("plateNumber", value)}
                  type="text"
                  value={form.plateNumber}
                />
                <TextField
                  label="Make"
                  name="make"
                  onChange={(value) => updateForm("make", value)}
                  type="text"
                  value={form.make}
                />
                <TextField
                  label="Model"
                  name="model"
                  onChange={(value) => updateForm("model", value)}
                  type="text"
                  value={form.model}
                />
                <TextField
                  label="Year"
                  name="year"
                  onChange={(value) => updateForm("year", value)}
                  type="number"
                  value={form.year}
                />
                <TextField
                  label="Color"
                  name="color"
                  onChange={(value) => updateForm("color", value)}
                  type="text"
                  value={form.color}
                />
                <TextField
                  label="VIN"
                  name="vin"
                  onChange={(value) => updateForm("vin", value)}
                  type="text"
                  value={form.vin}
                />
              </div>
            ) : null}

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <TextField
                label="Emergency contact name"
                name="emergencyContactName"
                onChange={(value) => updateForm("emergencyContactName", value)}
                type="text"
                value={form.emergencyContactName}
              />
              <TextField
                label="Emergency contact phone"
                name="emergencyContactPhone"
                onChange={(value) => updateForm("emergencyContactPhone", value)}
                type="tel"
                value={form.emergencyContactPhone}
              />
              <SelectField
                label="Blood type"
                name="bloodType"
                onChange={(value) => updateForm("bloodType", value)}
                options={[
                  { label: "Prefer not to say", value: "" },
                  { label: "A+", value: "A+" },
                  { label: "A-", value: "A-" },
                  { label: "B+", value: "B+" },
                  { label: "B-", value: "B-" },
                  { label: "AB+", value: "AB+" },
                  { label: "AB-", value: "AB-" },
                  { label: "O+", value: "O+" },
                  { label: "O-", value: "O-" },
                  { label: "Unknown", value: "unknown" },
                ]}
                value={form.bloodType}
              />
            </div>

            <div className="mt-5 grid gap-4">
              <TextAreaField
                label="Allergies"
                name="allergies"
                onChange={(value) => updateForm("allergies", value)}
                value={form.allergies}
              />
              <TextAreaField
                label="Medical conditions"
                name="medicalConditions"
                onChange={(value) => updateForm("medicalConditions", value)}
                value={form.medicalConditions}
              />
              <TextAreaField
                label="Regular medications"
                name="medications"
                onChange={(value) => updateForm("medications", value)}
                value={form.medications}
              />
              <TextAreaField
                label="Responder notes"
                name="responderNotes"
                onChange={(value) => updateForm("responderNotes", value)}
                value={form.responderNotes}
              />
            </div>
          </section>
        ) : null}

        {formError ? (
          <div className={`${BANNER_BASE} border-rose-300/70 bg-rose-100/70 text-rose-950`}>
            {formError}
          </div>
        ) : null}

        {!registrationBlocked ? (
          <Button
            disabled={
              submitStatus === "saving"
              || (needsAdultConsentNotice && noticeStatus === "loading")
            }
            type="submit"
          >
            {submitStatus === "saving"
              ? "Saving..."
              : guardianRequired
                ? "Save pending guardian approval"
                : "Save verified registration"}
          </Button>
        ) : null}
      </form>

      <aside className="space-y-5">
        {isBootstrapLookupPending ? (
          <PublicSidebarSectionSkeleton
            rows={3}
            testId="public-register-sidebar-skeleton"
          />
        ) : (
          <section className={SECTION_CLASS}>
            <h3 className="font-semibold">Lookup state</h3>
            <div className="mt-4 space-y-3 text-sm leading-6 text-muted-foreground">
              <p>Tag status: {lookupStatus}</p>
              <p>Tag type: {lookupData?.tagType ?? form.tagType}</p>
              <p>Activation: {lookupData?.activationState ?? "unresolved"}</p>
              {!isPlantationsRegistration ? (
                <p>
                  Registration status: {guardianRequired ? "guardian-required" : "adult"}
                </p>
              ) : null}
            </div>
          </section>
        )}

        <section className={SECTION_CLASS}>
          <h3 className="font-semibold">Verification status</h3>
          <ul className="mt-4 space-y-3 text-sm leading-6 text-muted-foreground">
            {guardianRequired ? (
              <>
                <li>Under-18 profiles stay pending until guardian approval completes.</li>
                <li>Minor registrations are not shown as activated or verified at this stage.</li>
                <li>Guardian contact accuracy matters because approval depends on it.</li>
              </>
            ) : (
              <>
                <li>Phone verification keeps your registration secure.</li>
                <li>The active consent notice is versioned and submitted with the registration.</li>
                <li>You can return here later to review or update your information.</li>
              </>
            )}
          </ul>
        </section>
      </aside>
    </section>
  )
}

function NoticeCard(props: {
  notice: ConsentNoticeVersionSummary | null
  sections: Array<{ body: string; heading: string }>
}) {
  if (!props.notice) {
    return null
  }

  const publishedLabel = props.notice.publishedAt
    ? new Intl.DateTimeFormat("en-IN", {
        dateStyle: "medium",
      }).format(new Date(props.notice.publishedAt))
    : null

  return (
    <div className="mt-4 rounded-3xl border border-border/70 bg-background/70 p-5">
      <div className="flex flex-wrap items-center gap-2 text-xs font-semibold tracking-[0.2em] text-muted-foreground uppercase">
        <span>{props.notice.version ?? "Current"}</span>
        {props.notice.locale ? <span>{props.notice.locale}</span> : null}
        {publishedLabel ? <span>Published {publishedLabel}</span> : null}
      </div>
      <h4 className="mt-3 text-lg font-semibold text-foreground">
        {props.notice.title ?? "SafetyQR compliance notice"}
      </h4>
      {props.notice.summary ? (
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          {props.notice.summary}
        </p>
      ) : null}
      {props.sections.length > 0 ? (
        <div className="mt-4 space-y-4">
          {props.sections.map((section) => (
            <div key={`${section.heading}-${section.body}`}>
              {section.heading ? (
                <h5 className="text-sm font-semibold text-foreground">
                  {section.heading}
                </h5>
              ) : null}
              {section.body ? (
                <p className="mt-1 text-sm leading-6 text-muted-foreground">
                  {section.body}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function SummaryStat(props: { label: string; value: string }) {
  return (
    <div className="rounded-3xl border border-border/70 bg-background/70 p-4">
      <dt className="text-xs tracking-[0.2em] text-muted-foreground uppercase">
        {props.label}
      </dt>
      <dd className="mt-2 text-base font-semibold text-foreground">
        {props.value}
      </dd>
    </div>
  )
}

function TextField(props: {
  label: string
  name: string
  onChange: (value: string) => void
  type: string
  value: string
}) {
  return (
    <label className="block">
      <span className={LABEL_CLASS}>{props.label}</span>
      <input
        className={INPUT_CLASS}
        name={props.name}
        onChange={(event) => props.onChange(event.target.value)}
        type={props.type}
        value={props.value}
      />
    </label>
  )
}

function TextAreaField(props: {
  label: string
  name: string
  onChange: (value: string) => void
  value: string
}) {
  return (
    <label className="block">
      <span className={LABEL_CLASS}>{props.label}</span>
      <textarea
        className={TEXTAREA_CLASS}
        name={props.name}
        onChange={(event) => props.onChange(event.target.value)}
        value={props.value}
      />
    </label>
  )
}

function SelectField(props: {
  disabled?: boolean
  label: string
  name: string
  onChange: (value: string) => void
  options: Array<{ label: string; value: string }>
  value: string
}) {
  return (
    <label className="block">
      <span className={LABEL_CLASS}>{props.label}</span>
      <select
        className={INPUT_CLASS}
        disabled={props.disabled}
        name={props.name}
        onChange={(event) => props.onChange(event.target.value)}
        value={props.value}
      >
        {props.options.map((option) => (
          <option key={option.value || option.label} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  )
}
